import { useEffect, useState, useMemo } from 'react';
import { DASHBOARD_TIME_BUTTONS } from '@bumble/app/constants';
import { useApi } from '@bumble/app/hooks/use-api';
import {
  Box,
  CardContent,
  Grid,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { DateTime, DurationLikeObject } from 'luxon';
import { toast } from 'react-toastify';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import DashboardTimeRange from '../DashboardTimeRange';
import { Loading } from '../Loading';
import bytes from 'bytes';
import { useCpe } from './CpeContext';
import { SessionTable, AppSummarySession } from '../pcap-analyzer/SessionTable';

interface ApplicationVisibilityDashboardProps {
  selectedTab: string;
}

const PIE_COLORS = [
  '#5F467E',
  '#6E9FCE',
  '#A7D46F',
  '#F9C74F',
  '#F9844A',
  '#90BE6D',
  '#43AA8B',
  '#577590'
];

export const ApplicationVisibilityDashboard = ({selectedTab}: ApplicationVisibilityDashboardProps) => {
  const [timeRange, setTimeRange] = useState<DurationLikeObject>(DASHBOARD_TIME_BUTTONS[0].value);
  const [chartHitsData, setChartHitsData] = useState<any[]>([]);
  const [chartDistributionData, setChartDistributionData] = useState<any[]>([]);
  const [chartBytesData, setChartBytesData] = useState<any[]>([]);
  const [totalApps, setTotalApps] = useState<number>(0);
  const [totalVisits, setTotalVisits] = useState<number>(0);
  const [totalBytes, setTotalBytes] = useState<number>(0);
  const [percentage, setPercentage] = useState<number>(0);
  const [sessions, setSessions] = useState<AppSummarySession[]>([]);
  const [appTypes, setAppTypes] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('Business Critical Apps');
  const [topAppsByCategory, setTopAppsByCategory] = useState<Record<string, any[]>>({});
  const [chartTopCategoryData, setChartTopCategoryData] = useState<any[]>([]);
  const { cpe, setCpe } = useCpe();
  const api = useApi();

  const { isLoading, data, refetch, fetchStatus, isRefetching } = useQuery(
    ['graph_data'],
    api.dashboardAppVisibilityApiV1DashboardAppVisibilityGet.bind(null, {
      node_id: cpe.nodeId,
      node_name: cpe.cpeName,
      start_time: DateTime.now().minus(timeRange).toISO(),
      end_time: DateTime.now().toISO(),
    }),
    { enabled: false },
  );

  const {
    data: typeData,
    refetch: typeRefetch,
  } = useQuery(
    ['type_data'],
    api.cpeProductsApiV1DashboardGetAppTypesGet.bind(null, {}),
    { enabled: false },
  );

  const handleCpeChange = (val : any) => {
    if (val) {
      setCpe({
        cpeName: val.label,
        nodeId: val.id
      })
    }
  };

  const handleTimeChange = (e: React.MouseEvent<HTMLElement>, val: DurationLikeObject) => {
    if (val) {
      setTimeRange(val);
    }
  };

  const handleRefreshClick = () => {
    if (cpe.cpeName && timeRange) {
      refetch();
      typeRefetch();
      toast.success('Refreshed data.');
    }
  };

  useEffect(() => {
    if (cpe.cpeName && timeRange) {
      refetch();
      typeRefetch();
    }
  }, [timeRange, cpe]);

  useEffect(() => {
    if (!typeData?.data) return;

    if (Array.isArray(typeData.data)) {
      setAppTypes(typeData.data);
      if (!selectedCategory || !typeData.data.includes(selectedCategory)) {
        const defaultCategory = typeData.data.includes('Business Critical Apps')
          ? 'Business Critical Apps'
          : typeData.data[0];
        setSelectedCategory(defaultCategory);
      }
    }
  }, [typeData]);


  useEffect(() => {
    if (!data?.data) return;

    const {
      top_app_by_hits: topAppByHits,
      top_app_by_bytes: topAppByBytes,
      total_apps,
      total_visits,
      total_bytes,
      sessions: respSessions = [],
      app_distribution,
      top_app_by_category,
    } = data.data as {
      top_app_by_hits: Array<{ app_name: string; hits: number }>;
      top_app_by_bytes: Array<{ app_name: string; bytes: number }>;
      total_apps: number;
      total_visits: number;
      total_bytes: number;
      sessions?: Array<{
        app_name: string;
        description?: string;
        port?: string;
        protocol?: string;
        hits: number;
        bytes: number;
        duration?: number;
      }>;
      app_distribution?: { [key: string]: string };
      top_app_by_category?: Record<string, any[]>;
    };

    setTotalApps(total_apps);
    setTotalBytes(total_bytes);
    setTotalVisits(total_visits);
    setChartHitsData(topAppByHits.map(n => ({
      name: n.app_name,
      hits: n.hits
    })));
    setChartBytesData(topAppByBytes.map(n => ({
      name: n.app_name,
      hits: n.bytes
    })));

    if (app_distribution) {
      const distData = Object.entries(app_distribution).map(([key, value]) => ({
        name: key,
        value: parseFloat(value.replace('%', '')),
      }));
      setChartDistributionData(distData);
    } else {
      setChartDistributionData([]);
    }

    if (top_app_by_category) {
      setTopAppsByCategory(top_app_by_category);
      const categoryData = top_app_by_category[selectedCategory] || [];
      setChartTopCategoryData(
        categoryData.slice(0, 5).map(item => ({
          name: item.application || item.app_name,
          bytes: item.bytes,
          description: item.description,
          protocol: item.protocol,
          port: item.port,
        }))
      );
    }

    const rows: AppSummarySession[] = (respSessions || []).map((s, i) => ({
      id: `app_${s.app_name}_${i}`,
      app_name: s.app_name,
      description: s.description ?? '',
      port: s.port ?? '',
      protocol: s.protocol ?? '',
      hits: s.hits ?? 0,
      bytes: s.bytes ?? 0,
      duration: s.duration ?? 0,
    }));
    setSessions(rows);
    setPercentage(0);
  }, [data]);

  const CustomAxisLabel = ({ x, y, payload } : any) => {
    const [labels, setLabels] = useState<string[]>([]);
    const splitIntoChunks = (input: string, length: number) => {
      const result = [];
      for (let i = 0; i < input.length; i += length) {
        result.push(input.slice(i, i + Math.min(input.length, length)));
      }
      return result;
    };

    useEffect(() => {
      const ary = splitIntoChunks(payload.value, 10);
      if (ary.length > 2) {
        setLabels([ary[0], `${ary[1].slice(0, 7)}...`]);
      } else {
        setLabels([...ary]);
      }
    }, [payload]);

    return (
      <text x={x} y={y} textAnchor="end" fill="#666" transform={`rotate(-35, ${x}, ${y})`}>
        {labels.length > 0 && <tspan x={x} dy="5">{labels[0]}</tspan>}
        {labels.length > 1 && <tspan x={x} dy="20">{labels[1]}</tspan>}
      </text>
    );
  }

  const handleCategoryChange = (event: any) => {
    const newCategory = event.target.value;
    setSelectedCategory(newCategory);
    const categoryData = topAppsByCategory[newCategory] || [];
    setChartTopCategoryData(
      categoryData.map(item => ({
        name: item.application || item.app_name,
        bytes: item.bytes,
        description: item.description,
        protocol: item.protocol,
        port: item.port,
      }))
    );
  };

  const selectedColor = useMemo(() => {
    const index = chartDistributionData.findIndex(item => item.name === selectedCategory);
    if (index === -1) return PIE_COLORS[0];
    return PIE_COLORS[index % PIE_COLORS.length];
  }, [selectedCategory, chartDistributionData]);

  const renderCategoryTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box sx={{ bgcolor: '#fff', p: 1.5, borderRadius: 1, boxShadow: 3 }}>
          <Typography variant="body2"><strong>Name:</strong> {data.name}</Typography>
          <Typography variant="body2"><strong>Bytes:</strong> {bytes.format(data.bytes)}</Typography>
          {data.description && (
            <Typography variant="body2"><strong>Description:</strong> {data.description}</Typography>
          )}
          {data.protocol && (
            <Typography variant="body2"><strong>Protocol:</strong> {data.protocol}</Typography>
          )}
          {data.port && (
            <Typography variant="body2"><strong>Port:</strong> {data.port}</Typography>
          )}
        </Box>
      );
    }
    return null;
  };

  return (
    <Box>
      <Box sx={{ mb: 0, py: 5, pb: 0 }}>
        <DashboardTimeRange
          timeValues={DASHBOARD_TIME_BUTTONS}
          selectedTime={timeRange}
          onCpeChange={handleCpeChange}
          onTimeChange={handleTimeChange}
          onRefresh={handleRefreshClick}
          selectedTab={selectedTab}
        />
      </Box>
      <Box>
        <Grid container spacing={5} sx={{ marginTop: 1, textAlign: 'center' }}>
          <Grid item xs={4} md={4}>
            <CardContent sx={{ bgcolor: '#fff', borderRadius: 1.2 }}>
              <Typography variant="h5">Total Apps</Typography>
              <Typography variant="h1" sx={{ marginTop: 2 }}>{totalApps.toLocaleString('en-US')}</Typography>
            </CardContent>
          </Grid>
          <Grid item xs={4} md={4}>
            <CardContent sx={{ bgcolor: '#fff', borderRadius: 1.2 }}>
              <Typography variant="h5">Total Visits</Typography>
              <Typography variant="h1" sx={{ marginTop: 2 }}>{totalVisits.toLocaleString('en-US')}</Typography>
            </CardContent>
          </Grid>
          <Grid item xs={4} md={4}>
            <CardContent sx={{ bgcolor: '#fff', borderRadius: 1.2 }}>
              <Typography variant="h5">Total Bytes</Typography>
              <Typography variant="h1" sx={{ marginTop: 2 }}>{bytes.format(totalBytes, {unitSeparator: ' '})}</Typography>
            </CardContent>
          </Grid>
        </Grid>
        <Grid container spacing={5} sx={{ marginTop: 1 }}>
          <Grid item sm={12} md={6}>
            <CardContent sx={{ bgcolor: '#fff', borderRadius: 1.2 }}>
              <Typography color="primary" sx={{ mb: 2 }}>Top Apps by Hits</Typography>
              {((isLoading || isRefetching) && fetchStatus !== 'idle') ? <Loading height={200} /> :
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart
                    data={chartHitsData}
                    margin={{ top: 5, right: 20, left: 30, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      angle={-35}
                      height={chartHitsData.length ? 80 : 0}
                      textAnchor="end"
                      tick={<CustomAxisLabel />}
                    />
                    <YAxis
                      orientation="left"
                      type="number"
                      width={30}
                      domain={chartHitsData.length ? undefined : [0, 100]}
                      tickFormatter={(value: number) => new Intl.NumberFormat('en-US', {
                        notation: 'compact',
                        compactDisplay: 'short',
                      }).format(value)}
                    />
                    {chartHitsData.length > 0 && <Tooltip />}
                    <Bar dataKey="hits" fill="rgb(95, 70, 126)" name="Top Apps By Hits" />
                  </BarChart>
                </ResponsiveContainer>}
            </CardContent>
          </Grid>
          <Grid item sm={12} md={6}>
            <CardContent sx={{ bgcolor: '#fff', borderRadius: 1.2 }}>
              <Typography color="primary" sx={{ mb: 2 }}>Top Apps by Bytes</Typography>
              {((isLoading || isRefetching) && fetchStatus !== 'idle') ? <Loading height={200} /> :
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart
                    data={chartBytesData}
                    margin={{ top: 5, right: 20, left: 50, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      textAnchor="end"
                      angle={-35}
                      tick={<CustomAxisLabel />}
                      height={chartBytesData.length ? 80 : 0}
                    />
                    <YAxis
                      orientation="left"
                      type="number"
                      width={30}
                      domain={chartBytesData.length ? undefined : [0, 100]}
                      tickFormatter={(value: number) => bytes.format(value)}
                    />
                    {chartBytesData.length && <Tooltip />}
                    <Bar dataKey="hits" fill="rgb(110, 159, 206)" name="Top Apps By Bytes" />
                  </BarChart>
                </ResponsiveContainer>
              }
            </CardContent>
          </Grid>
          <Grid item sm={12} md={6}>
            <CardContent sx={{ bgcolor: '#fff', borderRadius: 1.2 }}>
              <Typography color="primary" sx={{ mb: 2 }}>
                App Category Bandwidth
              </Typography>
              {((isLoading || isRefetching) && fetchStatus !== 'idle') ? (
                <Loading height={350} />
              ) : (
                <ResponsiveContainer width="100%" height={353}>
                  <PieChart>
                  <Pie
                    data={chartDistributionData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={130}
                    dataKey="value"
                    nameKey="name"
                    label={({ name, value, cx, cy, midAngle, outerRadius, index }) => {
                      if (value <= 1) return null;
                      const RADIAN = Math.PI / 180;
                      const radius = outerRadius + 10;
                      const x = cx + radius * Math.cos(-midAngle * RADIAN);
                      const y = cy + radius * Math.sin(-midAngle * RADIAN);
                      const fillColor = PIE_COLORS[index % PIE_COLORS.length];
                      return (
                        <text
                          x={x}
                          y={y}
                          fill={fillColor}
                          fontSize={12}
                          textAnchor={x > cx ? "start" : "end"}
                          dominantBaseline="central"
                        >
                          {`${name}: ${value.toFixed(2)}%`}
                        </text>
                      );
                    }}
                  >
                    {chartDistributionData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={PIE_COLORS[index % PIE_COLORS.length]}
                      />
                    ))}
                  </Pie>
                    <Tooltip formatter={(value: number) => `${value.toFixed(2)}%`} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Grid>
          <Grid item sm={12} md={6}>
            <CardContent sx={{ bgcolor: '#fff', borderRadius: 1.2 }}>
              <Box sx={{ display: 'inline-flex', gap: 2, }}>
                <Typography color="primary" sx={{ mt: 2 }}>
                  Top 5
                </Typography>
                <FormControl sx={{ minWidth: 300, mb: 3 }} size='small'>
                  <InputLabel id="category-select-label">Application Category</InputLabel>
                  <Select
                    labelId="category-select-label"
                    value={selectedCategory}
                    label="Application Category"
                    onChange={handleCategoryChange}
                  >
                    {appTypes.map(type => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {((isLoading || isRefetching) && fetchStatus !== 'idle') ? (
                <Loading height={321} />
              ) : (
                <ResponsiveContainer width="100%" height={333}>
                  <BarChart
                    data={chartTopCategoryData}
                    margin={{ top: 5, right: 20, left: 50, bottom: 30 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      angle={-35}
                      textAnchor="end"
                      height={80}
                      tick={<CustomAxisLabel />}
                    />
                    <YAxis
                      orientation="left"
                      type="number"
                      width={40}
                      tickFormatter={(value: number) => bytes.format(value)}
                    />
                    <Tooltip content={renderCategoryTooltip} />
                    <Bar dataKey="bytes" fill={selectedColor} name="Most Bandwidth Consumption" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Grid>
          <Grid item xs={12} md={12}>
            <CardContent sx={{ bgcolor: '#fff', borderRadius: 1.2 }}>
              <Typography color="primary" sx={{ mb: 2 }}>Applications View ({sessions.length})</Typography>
              {((isLoading || isRefetching) && fetchStatus !== 'idle') ? (
                <Loading height={200} />
              ) : (
                <SessionTable
                  variant="appVisibility"
                  percentage={percentage}
                  sessions={sessions}
                  title={cpe.cpeName || ''}
                />
              )}
            </CardContent>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};
